import React, { useState } from 'react';
import { FiMap, FiTrendingUp, FiTarget, FiCalendar } from 'react-icons/fi';

const ExpansionTab = ({ insightData, Section }) => {
  const [expansionExpanded, setExpansionExpanded] = useState(true);
  const onToggleExpansion = React.useCallback(() => setExpansionExpanded(v => !v), []);

  if (!insightData?.heptapod_dimensional_analysis?.expansion_intelligence) return null;

  const expansionIntel = insightData.heptapod_dimensional_analysis.expansion_intelligence;

  return (
    <>
      {/* Expansion Intelligence */}
      <Section title="Expansion Intelligence" iconComponent={FiMap} collapsible expanded={expansionExpanded} onToggle={onToggleExpansion}>
        {expansionExpanded && (
          <div className="space-y-4">
            {/* Growth Vectors */}
            {expansionIntel.growth_vectors && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Growth Vectors</h4>
                <div className="space-y-4">
                  {expansionIntel.growth_vectors.map((vector, idx) => (
                    <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                          <FiTrendingUp className="text-blue-600" />
                          <span className="font-semibold text-gray-700 text-sm">
                            {vector.vector_type?.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                            {vector.value_potential}
                          </span>
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                            {vector.probability_score}%
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-xs text-gray-600 mb-2">
                        {vector.opportunity_description}
                      </div>
                      
                      <div className="flex justify-between text-xs text-gray-500 mb-2">
                        <span>Timeline: {vector.timeline}</span>
                        <span>Probability: {vector.probability_score}%</span>
                      </div>
                      
                      {vector.requirements && (
                        <div className="text-xs text-gray-500">
                          <strong>Requirements:</strong> {vector.requirements.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Readiness Assessment */}
            {expansionIntel.readiness_assessment && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Readiness Assessment</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Budget Availability */}
                  {expansionIntel.readiness_assessment.budget_availability && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                      <div className="text-xs text-gray-700 font-semibold mb-1">BUDGET STATUS</div>
                      <div className="text-sm text-gray-800 font-bold mb-1">
                        {expansionIntel.readiness_assessment.budget_availability.status}
                      </div>
                      <div className="text-sm text-gray-700 font-semibold mb-1">
                        {expansionIntel.readiness_assessment.budget_availability.amount}
                      </div>
                      <div className="text-xs text-gray-600">
                        Timeline: {expansionIntel.readiness_assessment.budget_availability.decision_timeline}
                      </div>
                    </div>
                  )}

                  {/* Organizational Readiness */}
                  {expansionIntel.readiness_assessment.organizational_readiness && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="text-xs text-gray-600 font-semibold mb-2">ORGANIZATIONAL READINESS</div>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Change Capacity:</span>
                          <span className="text-gray-700 font-medium">
                            {expansionIntel.readiness_assessment.organizational_readiness.change_capacity}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Stakeholder Alignment:</span>
                          <span className="text-gray-700 font-medium">
                            {expansionIntel.readiness_assessment.organizational_readiness.stakeholder_alignment}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Resource Availability:</span>
                          <span className="text-gray-700 font-medium">
                            {expansionIntel.readiness_assessment.organizational_readiness.resource_availability}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Expansion Strategy */}
            {expansionIntel.expansion_strategy && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Expansion Strategy</h4>
                
                {/* Recommended Sequence */}
                {expansionIntel.expansion_strategy.recommended_sequence && (
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-gray-600 mb-3">Recommended Sequence</h5>
                    <div className="space-y-3">
                      {expansionIntel.expansion_strategy.recommended_sequence.map((step, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2">
                            <div className="font-semibold text-gray-700 text-sm">
                              Step {idx + 1}
                            </div>
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                              {step.timeline}
                            </span>
                          </div>
                          
                          <div className="text-xs text-gray-600 mb-2">
                            {step.step}
                          </div>
                          
                          <div className="text-xs text-gray-500 mb-2">
                            <strong>Rationale:</strong> {step.rationale}
                          </div>
                          
                          {step.success_criteria && (
                            <div className="text-xs text-gray-500">
                              <strong>Success Criteria:</strong> {step.success_criteria.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resource Requirements */}
                {expansionIntel.expansion_strategy.resource_requirements && (
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-gray-600 mb-3">Resource Requirements</h5>
                    <div className="flex flex-wrap gap-2">
                      {expansionIntel.expansion_strategy.resource_requirements.map((resource, idx) => (
                        <span key={idx} className="bg-blue-50 text-blue-800 px-3 py-1 rounded-full text-xs font-medium border border-blue-200">
                          {resource}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Risk Mitigation */}
                {expansionIntel.expansion_strategy.risk_mitigation && (
                  <div>
                    <h5 className="text-sm font-semibold text-gray-600 mb-3">Risk Mitigation</h5>
                    <div className="space-y-2">
                      {expansionIntel.expansion_strategy.risk_mitigation.map((mitigation, idx) => (
                        <div key={idx} className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-800">
                          {mitigation}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Section>
    </>
  );
};

export default ExpansionTab;
